/**
 * useLightboxGestures — native multi-touch gestures for image lightboxes.
 *
 * Behaviours (mobile-first, no library):
 *   • Pinch-to-zoom (2 fingers) — scales 1.0 ⇢ 4.0 around the pinch midpoint.
 *   • Pan (1 finger when zoomed in) — drag the image around to inspect details.
 *   • Swipe-down to dismiss (1 finger when NOT zoomed) — drag the image down
 *     by >120px or release with >0.6px/ms velocity to close.
 *   • Double-tap to toggle zoom (zooms to 2.5x at the tap point, or back to 1x).
 *   • Auto-reset on close.
 *   • Falls back to no-op on devices without touch (mouse desktop) so the
 *     overlay still closes via background-click / ESC / X button.
 *
 * Returns:
 *   - bind: spread onto the SCROLLABLE wrapper element ({onTouchStart, ...})
 *   - imgStyle: spread onto the <img> ({ transform, transition })
 *   - isZoomed: boolean — useful to disable swipe-to-dismiss UI affordances
 *   - reset(): manually reset zoom/pan (call this on close)
 */
import { useCallback, useEffect, useRef, useState } from "react";

const MAX_SCALE = 4;
const MIN_SCALE = 1;
const DBL_TAP_MS = 280;
const DISMISS_THRESHOLD_PX = 120;
const DISMISS_VELOCITY = 0.6; // px / ms

export function useLightboxGestures({ onDismiss, swipeOnly = false } = {}) {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  // dismissOffset is a *separate* downward drag value used only when NOT zoomed.
  // Keeps the dismiss gesture decoupled from pan-when-zoomed so the math
  // stays simple and the image returns to centered if the user cancels.
  const [dismissOffset, setDismissOffset] = useState(0);
  const [transitionOn, setTransitionOn] = useState(true);

  const stateRef = useRef({
    pinchStartDist: 0,
    pinchStartScale: 1,
    panStart: null,        // {x, y, t}
    initialTranslate: { x: 0, y: 0 },
    lastTap: 0,
  });

  const reset = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setDismissOffset(0);
    setTransitionOn(true);
  }, []);

  const distance = (t1, t2) => {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.hypot(dx, dy);
  };

  const onTouchStart = useCallback((e) => {
    setTransitionOn(false);
    if (!swipeOnly && e.touches.length === 2) {
      stateRef.current.pinchStartDist = distance(e.touches[0], e.touches[1]);
      stateRef.current.pinchStartScale = scale;
      stateRef.current.panStart = null;
    } else if (e.touches.length === 1) {
      const t = e.touches[0];
      const now = Date.now();
      // Double-tap detection — disabled in swipeOnly mode (charts use taps).
      if (!swipeOnly && now - stateRef.current.lastTap < DBL_TAP_MS) {
        setTransitionOn(true);
        if (scale > 1.1) {
          setScale(1);
          setTranslate({ x: 0, y: 0 });
        } else {
          setScale(2.5);
        }
        stateRef.current.lastTap = 0;
      } else {
        stateRef.current.lastTap = now;
      }
      stateRef.current.panStart = { x: t.clientX, y: t.clientY, t: now };
      stateRef.current.initialTranslate = { ...translate };
    }
  }, [scale, translate, swipeOnly]);

  const onTouchMove = useCallback((e) => {
    if (!swipeOnly && e.touches.length === 2) {
      // Pinch zoom
      const d = distance(e.touches[0], e.touches[1]);
      const base = stateRef.current.pinchStartDist || d;
      const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, stateRef.current.pinchStartScale * (d / base)));
      setScale(next);
      // When pinch crosses below 1.0, snap to centered.
      if (next <= 1.01) setTranslate({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && stateRef.current.panStart) {
      const t = e.touches[0];
      const dx = t.clientX - stateRef.current.panStart.x;
      const dy = t.clientY - stateRef.current.panStart.y;
      if (!swipeOnly && scale > 1.05) {
        // Pan while zoomed
        setTranslate({
          x: stateRef.current.initialTranslate.x + dx,
          y: stateRef.current.initialTranslate.y + dy,
        });
      } else if (dy > 0 && Math.abs(dy) > Math.abs(dx) * 1.4) {
        // Swipe-down dismiss preview.  Require a clear downward-dominant
        // gesture (dy > dx * 1.4) so horizontal chart pans don't trigger it.
        setDismissOffset(dy);
      }
    }
  }, [scale, swipeOnly]);

  const onTouchEnd = useCallback((e) => {
    setTransitionOn(true);
    // Dismiss check on swipe-down release
    if (stateRef.current.panStart && scale <= 1.05) {
      const elapsed = Date.now() - stateRef.current.panStart.t;
      const v = dismissOffset / Math.max(1, elapsed);
      if (dismissOffset > DISMISS_THRESHOLD_PX || v > DISMISS_VELOCITY) {
        onDismiss?.();
      } else {
        setDismissOffset(0); // snap back
      }
    }
    // Clamp scale on release
    if (scale < MIN_SCALE) {
      setScale(1);
      setTranslate({ x: 0, y: 0 });
    }
    if (e.touches.length === 0) {
      stateRef.current.panStart = null;
    }
  }, [scale, dismissOffset, onDismiss]);

  useEffect(() => {
    // Reset gestures whenever the consumer unmounts the lightbox.
    return reset;
  }, [reset]);

  const isZoomed = scale > 1.05;

  // Combine pan translate + dismiss offset.  Dismiss fades opacity as the
  // user pulls down so they get visual confirmation of the gesture.
  const opacity = dismissOffset > 0 ? Math.max(0.35, 1 - dismissOffset / 600) : 1;
  const imgStyle = {
    transform: `translate3d(${translate.x}px, ${translate.y + dismissOffset}px, 0) scale(${scale})`,
    transition: transitionOn ? "transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity 200ms linear" : "none",
    opacity,
    willChange: "transform",
    // In full lightbox mode we set touch-action:none so native zoom doesn't
    // fight pinch.  In swipeOnly mode we let the child (chart) handle its
    // own touch events; the wrapper's swipe-down detector still picks up
    // the dominant vertical drag regardless.
    ...(swipeOnly ? {} : { touchAction: "none" }),
  };

  return {
    bind: { onTouchStart, onTouchMove, onTouchEnd },
    imgStyle,
    isZoomed,
    reset,
  };
}

export default useLightboxGestures;
